function normalizeVoteAmount(value) {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount) || amount < 0) return 0;
  return Number(amount.toFixed(2));
}

function roundToCents(value) {
  return Number(Number(value || 0).toFixed(2));
}

export function computeVoteHistoryScale(totalVotes) {
  const total = normalizeVoteAmount(totalVotes);
  if (total <= 0) {
    return {
      yAxisMax: 10,
      ticks: [0, 2.5, 5, 7.5, 10]
    };
  }

  const step = total >= 100 ? 100 : 10;
  const yAxisMax = Math.ceil(total / step) * step;
  const interval = yAxisMax / 4;
  const ticks = Array.from({ length: 5 }, (_, index) => roundToCents(interval * index));

  return { yAxisMax, ticks };
}

export function buildMarketVoteHistory({ market, options, votes }) {
  if (!market) {
    throw new Error("Market is required.");
  }

  const startAt = new Date(market.startAt || market.start_at);
  const endAt = new Date(market.endAt || market.end_at);
  if (Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime())) {
    throw new Error("Market start/end times are invalid.");
  }

  const orderedOptions = Array.isArray(options) ? options : [];
  const safeVotes = Array.isArray(votes) ? votes : [];
  const cumulativeByOption = Object.fromEntries(orderedOptions.map((option) => [String(option.id), 0]));
  const seriesByOption = Object.fromEntries(
    orderedOptions.map((option) => [
      String(option.id),
      [{ ts: startAt.toISOString(), value: 0 }]
    ])
  );

  const sortedVotes = [...safeVotes].sort((a, b) => {
    const aTs = new Date(a.createdAt || a.created_at).getTime();
    const bTs = new Date(b.createdAt || b.created_at).getTime();
    if (aTs !== bTs) return aTs - bTs;
    return String(a.id || "").localeCompare(String(b.id || ""));
  });

  for (const vote of sortedVotes) {
    const optionId = String(vote.optionId || vote.option_id || "");
    if (!Object.hasOwn(seriesByOption, optionId)) continue;

    const voteTs = new Date(vote.createdAt || vote.created_at);
    if (Number.isNaN(voteTs.getTime())) continue;

    const amount = normalizeVoteAmount(vote.voteAmount || vote.vote_amount);
    cumulativeByOption[optionId] = roundToCents(cumulativeByOption[optionId] + amount);
    seriesByOption[optionId].push({
      ts: voteTs.toISOString(),
      value: cumulativeByOption[optionId]
    });
  }

  const optionsWithSeries = orderedOptions.map((option) => {
    const optionId = String(option.id);
    const series = seriesByOption[optionId] || [{ ts: startAt.toISOString(), value: 0 }];
    const finalValue = series[series.length - 1]?.value || 0;
    const anchoredSeries = [...series];
    const endIso = endAt.toISOString();
    if (anchoredSeries[anchoredSeries.length - 1]?.ts !== endIso) {
      anchoredSeries.push({ ts: endIso, value: finalValue });
    }

    return {
      id: option.id,
      label: option.label,
      series: anchoredSeries
    };
  });

  const totalVotes = roundToCents(
    optionsWithSeries.reduce((sum, option) => sum + (option.series[option.series.length - 1]?.value || 0), 0)
  );
  const { yAxisMax, ticks } = computeVoteHistoryScale(totalVotes);

  return {
    marketId: market.id,
    startAt: startAt.toISOString(),
    endAt: endAt.toISOString(),
    totalVotes,
    yAxisMax,
    ticks,
    options: optionsWithSeries
  };
}
